/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { injectOptional } from "@/tsyringe-extend"
import { message } from "ant-design-vue"
import axios, { Axios, AxiosError, type AxiosInstance, type AxiosRequestConfig, type AxiosRequestHeaders, type AxiosResponse } from "axios"
import { container, instanceCachingFactory, registry, singleton } from "tsyringe"
import type { App, InjectionKey } from "vue"
import { CORE_AXIOS, CORE_INITIALIZER, CORE_USER } from "../CoreSymbol"
import type { Initializer } from "./Initializer"
import { type UserService } from "./UserInitializer"
import type { LoginPageOption, LoginPageService, Plugin } from '@/plugins'
import { PLUGIN_AUTH } from "@/plugins/builtin/BuiltinPluginSymbol"
import Cookie from 'js-cookie'
import { RESETPASSWORD_PATH, SIGNIN_PATH, SIGNUP_PATH } from '@/plugins/builtin/login_page/constants'

export const AxiosOptionsInjectKey = "AxiosOptionsInjectKey"

export type AxiosOption = Partial<AxiosRequestConfig> & { port?: number }

/**
 * 包装路由的初始化，并注册一个路由实例供其他模块使用
 */
@singleton()
@registry([
  {
    token: CORE_INITIALIZER,
    useFactory: instanceCachingFactory<Initializer>((c) => c.resolve(AxiosInitializer))
  }
])
export class AxiosInitializer implements Initializer {
  options: AxiosOption
  constructor(
    @injectOptional(AxiosOptionsInjectKey)
    options: AxiosOption
  ) {
    this.options = options
    let baseURL = options.baseURL
    if (!baseURL) {
      if (window) {
        const location = window.location
        baseURL = `${location.protocol}//${location.hostname}:${options.port || 9090}/`
      }
    }
    this.options.baseURL = baseURL || `http://localhost:${options.port || 9090}/`
  }

  start(app: App<Element>): void {
    const http = new Http(this.options)
    app.config.globalProperties.$axios = http
    app.provide(AXIOSKEY, http)
    container.register<Http>(CORE_AXIOS, { useValue: http })
  }

  started(): void {
    //
  }
}

/**
 * @title   http请求封装
 * desc 前后端约定接口返回解构规范
 * {
 *    code: '1',
 *    data:" 请求成功",
 *    message: ""
 * }
 */
export class Http {
  public axios: Axios
  public options: AxiosRequestConfig
  constructor(
    @injectOptional(AxiosOptionsInjectKey)
    options: AxiosRequestConfig
  ) {
    this.options = options || {}
    // 获取axios实例
    this.axios = new Interceptor(options).getInterceptors()
  }

  /**
   * get请求
   * @param {String} url [请求的url地址]
   * @param {object} params [请求时携带的参数, 默认为空]
   * @param {
   *   {boolean} download [判断是否是下载请求， 默认为否]
   *   {boolean} loading [判断是否是需要loading， 默认为否]
   *   {number[]} messageStatus [自定义控制接口code状态]
   *   {function} message [自定义接口请求之后的方法，与messageStatus配合使用]
   * } options [自定义配置项]
   */
  public get<T>(
    url: string,
    params: object = {},
    options?: {
      download?: boolean
      loading?: boolean
      messageStatus?: Array<number>
      message?: () => void
      raw?: boolean;
      [x: string]: any
    }
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const config: AxiosRequestConfig = { params }
      if (options?.download) {
        config.responseType = "blob"
      }
      this.axios
        .get(url, {
          ...config
        })
        .then((res: AxiosResponse<T>) => {
          if (options?.raw) {
            resolve(res as T)
          } else {
            resolve(res.data as T)
          }
          this.resultHandle(res, options as Partial<typeof options>)
        })
        .catch((err: AxiosResponse) => {
          if (options?.raw) {
            reject(err as T)
          } else {
            reject(err.data as T)
          }
          this.resultHandle(err, options as Partial<typeof options>)
        })
    })
  }

  /**
   * post请求
   * @param {String} url [请求的url地址]
   * @param {object} data [请求时携带的参数, 默认为空]
   * @param {
   *   {boolean} download [判断是否是下载请求， 默认为否]
   *   {boolean} loading [判断是否是需要loading， 默认为否]
   *   {object} headers [自定义头部信息， 默认为空]
   *   {boolean} formData [formData格式， 默认为否]
   *   {number[]} messageStatus [自定义控制接口code状态]
   *   {function} message [自定义接口请求之后的方法，与messageStatus配合使用]
   * } options [自定义配置项]
   */
  public post<T>(
    url: string,
    data: object = {},
    options?: {
      download?: false
      loading?: false
      formData?: false
      // eslint-disable-next-line @typescript-eslint/ban-types
      headers?: {}
      messageStatus?: Array<number>
      message?: () => void
      raw?: boolean
    }
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const config: AxiosRequestConfig = {}
      const headers = options?.headers
      const newData = data
      if (options?.download) {
        config.responseType = "blob"
        config.headers = { "Content-Type": "text/plain" }
      }

      if (options?.formData) {
        //  newData = qs.stringify(data);
        config.headers = { "Content-Type": "application/x-www-form-urlencoded" }
      }

      this.axios
        .post(url, (data = newData), {
          ...config,
          headers
        })
        .then((res: AxiosResponse<T>) => {
          if (options?.raw) {
            resolve(res as any)
          } else {
            resolve(res.data as any)
          }
          this.resultHandle(res, options as Partial<typeof options>)
        })
        .catch((err: AxiosResponse) => {
          if (options?.raw) {
            reject(err as T)
          } else {
            reject(err.data as T)
          }
          this.resultHandle(err, options as Partial<typeof options>)
        })
    })
  }

  /**
   * put请求
   * @param {String} url [请求的url地址]
   * @param {object} data [请求时携带的参数, 默认为空]
   * @param {
   *   {boolean} loading [判断是否是需要loading， 默认为否]
   *   {number[]} messageStatus [自定义控制接口code状态]
   *   {function} message [自定义接口请求之后的方法，与messageStatus配合使用]
   * } options [自定义配置项]
   */
  public put<T>(
    url: string,
    data: object = {},
    options?: {
      loading?: false
      messageStatus?: Array<number>
      message?: () => void
    }
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const config: AxiosRequestConfig = { ...data }

      this.axios
        .put(url, {
          ...config
        })
        .then((res: AxiosResponse<T>) => {
          resolve(res.data as T)
          this.resultHandle(res, options as Partial<typeof options>)
        })
        .catch((err: AxiosResponse) => {
          reject(err.data)
          this.resultHandle(err, options as Partial<typeof options>)
        })
    })
  }

  /**
   * delete请求
   * @param {String} url [请求的url地址]
   * @param {object} data [请求时携带的参数, 默认为空]
   * @param {
   *   {boolean} loading [判断是否是需要loading， 默认为否]
   *   {number[]} messageStatus [自定义控制接口code状态]
   *   {function} message [自定义接口请求之后的方法，与messageStatus配合使用]
   * } options [自定义配置项]
   */
  public delete<T>(
    url: string,
    data: object = {},
    options?: {
      loading?: false
      messageStatus?: Array<number>
      message?: () => void
    }
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const config: AxiosRequestConfig = { data }

      config.headers = { "Content-Type": "application/json:charset=UTF-8" }

      this.axios
        .delete(url, {
          ...config
        })
        .then((res: AxiosResponse<T>) => {
          resolve(res.data as T)
          this.resultHandle(res, options as Partial<typeof options>)
        })
        .catch((err: AxiosResponse) => {
          reject(err.data)
          this.resultHandle(err, options as Partial<typeof options>)
        })
    })
  }

  /**
   * resultHandle 根据响应code
   * @param {any} res [请求返回值]
   * @param {any} resolve [Promise.resolve]
   * @param {boolean} loading [判断是否是需要loading]
   */
  public resultHandle(
    responseData: AxiosResponse | AxiosError,
    options?: {
      download?: boolean
      loading?: boolean
      messageStatus?: Array<number>
      message?: () => void
    }
  ) {
    if (options) {
      const { messageStatus, message } = options
      const messageFunc = message as () => void
      // eslint-disable-next-line array-callback-return
      messageStatus?.map((stateNumber) => {
        if (responseData.status === stateNumber) {
          if (typeof messageFunc === "function") {
            messageFunc()
          } else {
            throw new Error("missed message function,please checking")
          }
        }
      })
    }
  }
}

/**
 *  @title 拦截器封装
 */
class Interceptor {
  /**
   * http握手错误
   * @param res  响应回调,根据不同响应进行不同操作
   */
  private static errorHandle(res: AxiosError) {
    // 状态码判断
    switch (res.status) {
      case "400":
        break
      case "401":
        break
      case "403":
        break
      case "409":
        break
      case "404":
        message.error("请求的资源不存在")
        break
      default:
        message.error("连接错误")
    }
  }

  public instance: AxiosInstance

  constructor(options: AxiosRequestConfig) {
    // 创建axios实例
    this.instance = axios.create({ ...options, timeout: 50000 * 12, withCredentials: true })
    // 初始化拦截器
    this.initInterceptors()
  }

  // 为了让http.ts中获取初始化好的axios实例
  public getInterceptors() {
    return this.instance
  }

  // 初始化拦截器
  public initInterceptors() {
    // 设置post请求头
    this.instance.defaults.headers.post["Content-Type"] = "application/x-www-form-urlencoded"
    // this.instance.defaults.baseURL = '';
    this.instance.defaults.withCredentials = true
    /**
     * 请求拦截器
     * 每次请求前，如果存在token则在请求头中携带token
     */
    this.instance.interceptors.request.use(
      (config) => {
        // 登录流程控制中，根据本地是否存在token判断用户的登录情况
        // 但是即使token存在，也有可能token是过期的，所以在每次的请求头中携带token
        // 后台根据携带的token判断用户的登录情况，并返回给我们对应的状态码
        let path = window.location.pathname
        if (path == SIGNIN_PATH || path == RESETPASSWORD_PATH || path == SIGNUP_PATH || !/^\/console/.test(path)) {
          sessionStorage.removeItem('reload')
          return config
        }
        // 如果是登录页面接口，则无须理会
        const userService = container.resolve<UserService>(CORE_USER)
        const loginOptions = container.resolve<Plugin<LoginPageOption, LoginPageService>>(PLUGIN_AUTH)
        const isUserValid = userService.isUserValid()
        const cookie = Cookie.get(loginOptions.options.cookieKey || 'iota')
        const isLogged = isUserValid && cookie && cookie.length > 0

        // 未登录
        if (!isLogged) {
          sessionStorage.setItem('reload', 'need')
          message.warn({
            content: '用户信息过期，请先登录',
            key: 'unauthorized',
            style: {
              marginTop: '10vh',
            },
            onClose() {
              path = window.location.pathname
              if (path != SIGNIN_PATH && path != RESETPASSWORD_PATH && path != SIGNUP_PATH) {
                // 重新刷新
                const needReload = sessionStorage.getItem('reload')
                if (needReload)
                  window.location.reload()
              }
            },
          })
        }

        const token = sessionStorage.getItem("ymNavMap_token")
        if (token) {
          (config.headers as AxiosRequestHeaders).Authorization = token
        }
        return config
      },
      (error: AxiosError) => {
        console.log(error)
      }
    )

    // 响应拦截器
    this.instance.interceptors.response.use(
      // 请求成功
      (responeBody) => {
        if (responeBody.status === 200 || responeBody.status === 201 || responeBody.status === 302 || responeBody.status === 304 || responeBody.status === 204) {
          return Promise.resolve(responeBody)
        } else {
          // Interceptor.errorHandle(res)
          return Promise.reject(responeBody)
        }
      },
      // 请求失败
      (error: AxiosError) => {
        const { response } = error
        if (response) {
          // 请求已发出，但是不在2xx的范围
          // Interceptor.errorHandle(response)
          return Promise.reject(response)
        } else {
          // 处理断网的情况
          // eg:请求超时或断网时，更新state的network状态
          // network状态在app.vue中控制着一个全局的断网提示组件的显示隐藏
          // 关于断网组件中的刷新重新获取数据，会在断网组件中说明
          message.error("网络连接异常,请稍后再试!")
        }
      }
    )
  }
}

export const AXIOSKEY: InjectionKey<Http> = Symbol("AxiosKey")
